// src/utils/useRaces.ts
//
// Seznam závodů pro obrazovky: JEDNO čtení kolekce `races` na stránku,
// sdílené všemi instancemi hooku (UserResultsPage + UserResults ji dosud
// četly dvakrát na jedné stránce), a čtení publikovaných výsledků závodu
// s localStorage cache `lks_results_{id}`.
//
// Seznam se drží v paměti relace jako stale-while-revalidate: při mountu
// hooku se ukáže hned (status se odvozuje z dat v paměti, nic nebliká) a
// zároveň se VŽDY načte znovu z Firestore – souběžné instance na jedné
// stránce sdílejí jeden rozběhnutý dotaz (racesInflight). Jen tak se na
// /results projeví nová publikace výsledků (resultsPublishedAt = klíč
// invalidace cache výsledků) a změna stavu live závodu (karty LIVE /
// ukončeno); seznam načtený jednou za relaci obojí zmrazil. Po návratu na
// záložku (visibilitychange → visible) se seznam obnoví také, nejdřív po
// RACES_REFRESH_MIN_MS od posledního úspěšného načtení.
//
// Dotaz je sjednocený: celá kolekce bez where/orderBy (tak ji četly obě
// obrazovky – seznam je malý a admin může kdykoli změnit enableResults /
// includeInResults, proto se nic nedrží v localStorage), filtry rok /
// disciplína se dělají v paměti, řazení podle data sestupně (nejnovější
// první – jako UserResults). Testovací závod (generálka, testRace) se do
// seznamu vůbec nedostane.
//
// Cache výsledků (klíč, obsah i invalidace) je PŘESUN 1:1 z UserResults
// („nesmí se změnit"): klíč `lks_results_{raceId}`, hodnota JSON
// { ts, pub, data }, platná max. 1 h a jen když `pub` == races.resultsPublishedAt
// (millis) – (pře)publikace v adminu tak cache zneplatní na všech zařízeních.
// Chyba čtení Firestore se PROPAGUJE (dřív se tiše vracelo [] = „bez výsledků").

import { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getCountFromServer, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import type { AsyncStatus } from "../ui/AsyncState";
import { toDisciplineId, type DisciplineId } from "./disciplines";
import { reportError, reportWarning } from "./reportError";
import { visibleToMembers } from "./testRace";
import {
  sortRaceEntries,
  type RaceResult,
  type RaceResultEntry,
  type RaceStageResult,
  type SeasonRace,
} from "./seasonRanking";

// ─────────────────────────────────────────────────────────────
//  Typ závodu
// ─────────────────────────────────────────────────────────────

/** Závod z kolekce races v tvaru pro výsledky a live karty (pole, která obrazovky čtou). */
export interface Race extends SeasonRace {
  id: string;
  name: string;
  /** "YYYY-MM-DD" tak, jak je uloženo. */
  date: string;
  /** Rok z data; 0 při nečitelném datu. */
  year: number;
  /** Starší závody bez pole = kulovnice (toDisciplineId). */
  discipline: DisciplineId;
  /** Započítat do ročního hodnocení (výchozí true). */
  includeInResults: boolean;
  /** false = akce bez výsledků (schůze, DOD…) – nezobrazuje se ve výsledcích. */
  enableResults: boolean;
  /** Millis poslední publikace výsledků (races.resultsPublishedAt); 0 = neznámo. Klíč invalidace cache. */
  resultsPublishedAt: number;
  /** Počet publikovaných střelců (races.resultsPublishedCount); 0 = neznámo (např. starší import) → countRaceResults(). */
  resultsPublishedCount: number;
  /** "active" | "finished" | "" (starší závody pole nemají). */
  status: string;
  liveScoring: boolean;
  /** Veřejná live tabule (races.livePublic !== false). */
  livePublic: boolean;
  /** Millis ukončení live závodu (races.finishedAt); 0 = není. */
  finishedAt: number;
}

/** Millis z Timestamp / {seconds} / ISO řetězce / Date; jinak 0. */
function toMillis(value: unknown): number {
  if (!value) return 0;
  const v = value as { toMillis?: () => number; toDate?: () => Date; seconds?: number };
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.toDate === "function") return v.toDate().getTime();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  const ms = new Date(value as string | number | Date).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/** Dokument races → Race (chybějící pole dostanou výchozí hodnoty). */
export function mapRaceDoc(id: string, data: Record<string, unknown>): Race {
  const date = typeof data.date === "string" ? data.date : "";
  const year = new Date(date).getFullYear();
  return {
    id,
    name: typeof data.name === "string" ? data.name : "",
    date,
    year: Number.isFinite(year) ? year : 0,
    discipline: toDisciplineId(data.discipline),
    includeInResults: data.includeInResults !== false,
    enableResults: data.enableResults !== false,
    resultsPublishedAt: toMillis(data.resultsPublishedAt),
    resultsPublishedCount:
      typeof data.resultsPublishedCount === "number" ? data.resultsPublishedCount : 0,
    status: typeof data.status === "string" ? data.status : "",
    liveScoring: data.liveScoring === true,
    livePublic: data.livePublic !== false,
    finishedAt: toMillis(data.finishedAt),
    // Počet míst rozhodovaných rozstřelem (pravidla shody 4b, tieRules.shootOffPlacesOf)
    shootOffPlaces: typeof data.shootOffPlaces === "number" ? data.shootOffPlaces : undefined,
  };
}

/** Řazení seznamu: podle data sestupně (nečitelné datum na konec). */
export function sortRacesNewestFirst(races: Race[]): Race[] {
  const ms = (r: Race) => {
    const t = new Date(r.date).getTime();
    return Number.isFinite(t) ? t : -Infinity;
  };
  return [...races].sort((a, b) => ms(b) - ms(a));
}

export interface RaceFilter {
  year?: number;
  discipline?: DisciplineId | "all";
}

/** Filtr rok / disciplína v paměti (undefined nebo "all" = bez filtru). */
export function filterRaces(races: Race[], filter: RaceFilter = {}): Race[] {
  const { year, discipline } = filter;
  return races.filter(
    (r) =>
      (year === undefined || r.year === year) &&
      (discipline === undefined || discipline === "all" || r.discipline === discipline)
  );
}

/** Okno, po které se ukončený live závod ještě nabízí jako „výsledky jsou finální". */
export const LIVE_FINISHED_WINDOW_MS = 2 * 24 * 60 * 60 * 1000;

/**
 * Závody pro info karty nad výsledky – 1:1 s dosavadní UserResultsPage:
 * jen s výsledky a live scoringem; `live` = probíhající, `finished` =
 * ukončené před méně než 2 dny. Neveřejná tabule (livePublic false) se
 * nenabízí ani u ukončených (plán kroku 7).
 */
export function selectLiveRaces(
  races: Race[],
  now: number = Date.now()
): { live: Race[]; finished: Race[] } {
  const candidates = races.filter((r) => r.enableResults && r.liveScoring && r.livePublic);
  return {
    live: candidates.filter((r) => r.status === "active"),
    finished: candidates.filter(
      (r) =>
        r.status === "finished" &&
        r.finishedAt > 0 &&
        now - r.finishedAt < LIVE_FINISHED_WINDOW_MS
    ),
  };
}

// ─────────────────────────────────────────────────────────────
//  Seznam závodů v paměti relace (sdílený mezi instancemi hooku)
// ─────────────────────────────────────────────────────────────

interface RacesState {
  /** null = v této relaci ještě nenačteno (nebo zapomenuto přes invalidateRaces). */
  races: Race[] | null;
  loading: boolean;
  /** Chyba posledního čtení; null po úspěchu. */
  error: unknown;
}

let racesState: RacesState = { races: null, loading: false, error: null };
let racesInflight: Promise<Race[]> | null = null;
/** Millis posledního úspěšného načtení (0 = ještě nikdy) – odstup obnovy po návratu na záložku. */
let racesLoadedAt = 0;
const racesListeners = new Set<(state: RacesState) => void>();

function setRacesState(patch: Partial<RacesState>): void {
  racesState = { ...racesState, ...patch };
  racesListeners.forEach((listener) => listener(racesState));
}

/** Nejmenší odstup mezi obnovami seznamu při návratu na záložku (visibilitychange). */
export const RACES_REFRESH_MIN_MS = 30 * 1000;

/**
 * Seznam závodů načtený dřív v této relaci (nejnovější první), nebo null –
 * pro odvození výchozích hodnot ještě v inicializátoru stavu obrazovky
 * (výchozí rok ve výsledcích), ať po návratu na stránku nic nebliká.
 */
export function getLoadedRaces(): Race[] | null {
  return racesState.races;
}

/**
 * Načte seznam závodů: bez `force` vrátí seznam z paměti relace, je-li;
 * `force` čte čerstvě z Firestore. Souběžná volání sdílejí jeden dotaz
 * (dedup souběhu – dvě instance hooku na stránce = jedno čtení). Chybu
 * propaguje a zároveň ji uloží do stavu pro hook.
 */
export function loadRaces(force = false): Promise<Race[]> {
  if (!force && racesState.races) return Promise.resolve(racesState.races);
  if (racesInflight) return racesInflight;

  setRacesState({ loading: true, error: null });
  const run = getDocs(collection(db, "races"))
    .then((snap) => {
      // Testovací závod (generálka) členové nevidí – výsledky, karty živě,
      // roční hodnocení ani Moje sezóna (utils/testRace.ts)
      const races = sortRacesNewestFirst(
        snap.docs
          .filter((d) => visibleToMembers(d.data() as Record<string, unknown>))
          .map((d) => mapRaceDoc(d.id, d.data() as Record<string, unknown>))
      );
      racesLoadedAt = Date.now();
      setRacesState({ races, loading: false, error: null });
      // Jednorázový úklid staré cache seznamu závodů (dřív v UserResults).
      try {
        localStorage.removeItem("lks_races_list");
      } catch {
        // localStorage nedostupné – nic
      }
      return races;
    })
    .catch((err: unknown) => {
      reportError("useRaces.loadRaces", err);
      setRacesState({ loading: false, error: err });
      throw err;
    })
    .finally(() => {
      if (racesInflight === run) racesInflight = null;
    });
  racesInflight = run;
  return run;
}

/** Obnova na pozadí: chyba je ve stavu hooku (reportError ji zalogoval), promise nikdy neodmítá. */
const refreshRaces = (): Promise<void> => loadRaces(true).then(() => undefined, () => undefined);

/** Obnova po návratu na záložku – jen když je poslední načtení starší než RACES_REFRESH_MIN_MS. */
function refreshRacesIfStale(): void {
  if (Date.now() - racesLoadedAt >= RACES_REFRESH_MIN_MS) refreshRaces();
}

/**
 * Seznam v paměti je neplatný (po uložení závodu v administraci). Mountnuté
 * hooky dál ukazují dosavadní seznam a dostanou čerstvý (běží-li zrovna
 * čtení, zopakuje se po něm – mohlo začít před zápisem); bez posluchačů se
 * seznam jen zapomene a příští mount ho načte.
 */
export function invalidateRaces(): void {
  if (racesListeners.size === 0) {
    setRacesState({ races: null });
    return;
  }
  if (racesInflight) racesInflight.then(refreshRaces, refreshRaces);
  else refreshRaces();
}

/** Stabilní prázdné pole, ať se useMemo níže zbytečně nepřepočítává. */
const EMPTY_RACES: Race[] = [];

export interface UseRacesResult {
  /** Závody po filtru (rok / disciplína), nejnovější první. */
  races: Race[];
  /** Všechny závody bez filtru (pro seznam roků apod.). */
  allRaces: Race[];
  /**
   * "loading" do prvního načtení v relaci, "error" když se nepodařilo a není
   * co ukázat, "empty" když po filtru nic nezbylo, jinak "ready". Při obnově
   * na pozadí (mount, návrat na záložku, reload) zůstává stav podle
   * dosavadních dat – `error` je přesto nastavené.
   */
  status: AsyncStatus;
  error: unknown;
  /** Čerstvé načtení z Firestore; nikdy nevyhazuje (chyba je ve `status`/`error`). */
  reload: () => Promise<void>;
}

/**
 * Hook: seznam závodů ze sdílené paměti relace (stale-while-revalidate).
 * Každý mount ukáže dosavadní seznam a spustí čerstvé čtení; instance
 * mountnuté naráz (UserResultsPage + UserResults) sdílejí jeden dotaz a
 * výsledek kterékoli obnovy dostanou všechny. Po návratu na záložku se
 * seznam obnoví znovu (s odstupem RACES_REFRESH_MIN_MS).
 */
export function useRaces(options: RaceFilter = {}): UseRacesResult {
  const [state, setState] = useState<RacesState>(racesState);
  const { year, discipline } = options;

  useEffect(() => {
    racesListeners.add(setState);
    setState(racesState);
    refreshRaces();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshRacesIfStale();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      racesListeners.delete(setState);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const reload = useCallback(refreshRaces, []);

  const allRaces = state.races ?? EMPTY_RACES;
  const races = useMemo(
    () => filterRaces(allRaces, { year, discipline }),
    [allRaces, year, discipline]
  );

  let status: AsyncStatus;
  if (state.races) status = races.length === 0 ? "empty" : "ready";
  else if (state.error) status = "error";
  else status = "loading";

  return { races, allRaces, status, error: state.error, reload };
}

// ─────────────────────────────────────────────────────────────
//  Výsledky závodu + localStorage cache lks_results_{id}
// ─────────────────────────────────────────────────────────────

/** Cache výsledků platí hodinu (a jen pro stejné resultsPublishedAt). */
export const RESULTS_CACHE_TTL_MS = 60 * 60 * 1000;

export const resultsCacheKey = (raceId: string): string => `lks_results_${raceId}`;

/** Smaže cache výsledků závodu v tomto prohlížeči (po publikaci/importu). */
export function clearRaceResultsCache(raceId: string): void {
  try {
    localStorage.removeItem(resultsCacheKey(raceId));
  } catch {
    // localStorage nedostupné – nic
  }
}

export interface RaceResultsBundle {
  raceId: string;
  /** Výsledky ve výchozím řazení (sortRaceEntries); prázdné pole = závod bez výsledků. */
  results: RaceResult[];
  /** True = z localStorage cache (bez dotazu do Firestore). */
  fromCache: boolean;
}

/**
 * Rozběhnutá čtení podle závodu A publikace: čtení pro novější
 * resultsPublishedAt nesmí dostat výsledek dotazu rozběhnutého pro starší.
 */
const resultsInflight = new Map<string, Promise<RaceResultsBundle>>();

const resultsInflightKey = (raceId: string, resultsPublishedAt: number): string =>
  `${raceId}@${resultsPublishedAt}`;

/** Uložené výsledky z cache, když jsou čerstvé a ze stejné publikace; jinak null (a cache se smaže). */
function readResultsCache(raceId: string, resultsPublishedAt: number): RaceResult[] | null {
  const cacheKey = resultsCacheKey(raceId);
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { ts, pub, data } = JSON.parse(cached);
      const freshEnough = Date.now() - ts < RESULTS_CACHE_TTL_MS;
      const samePublish = (pub ?? 0) === resultsPublishedAt;
      if (freshEnough && samePublish && Array.isArray(data)) return data as RaceResult[];
      localStorage.removeItem(cacheKey);
    }
  } catch (err) {
    reportWarning("loadRaceResults.cache", err, { raceId });
  }
  return null;
}

/**
 * Výsledky závodu (results_entries + results_stages) s cache lks_results_{id}.
 * `resultsPublishedAt` = Race.resultsPublishedAt (millis, 0 = neznámo) – musí
 * sedět s uloženým `pub`, jinak se čte znovu. Souběžná volání pro stejný závod
 * a stejnou publikaci sdílejí jeden dotaz (dřív se po rozbalení během
 * auto-načtení roku četlo dvakrát); volání s novější publikací si čte samo.
 * Chyba Firestore se propaguje – volající ukáže ErrorState s retry.
 */
export function loadRaceResults(
  raceId: string,
  resultsPublishedAt: number = 0
): Promise<RaceResultsBundle> {
  const cached = readResultsCache(raceId, resultsPublishedAt);
  if (cached) return Promise.resolve({ raceId, results: cached, fromCache: true });

  const inflightKey = resultsInflightKey(raceId, resultsPublishedAt);
  const pending = resultsInflight.get(inflightKey);
  if (pending) return pending;

  const run = (async (): Promise<RaceResultsBundle> => {
    const [entriesSnap, stagesSnap] = await Promise.all([
      getDocs(collection(db, `races/${raceId}/results_entries`)),
      getDocs(collection(db, `races/${raceId}/results_stages`)),
    ]);
    const stagesMap: { [seq: string]: RaceStageResult[] } = {};
    stagesSnap.forEach((docSnap) => {
      stagesMap[docSnap.id] = (docSnap.data() as { stages?: RaceStageResult[] }).stages || [];
    });
    const results = sortRaceEntries(
      entriesSnap.docs.map((docSnap) => {
        const entry = docSnap.data() as RaceResultEntry;
        return { entry, stages: stagesMap[entry.sequenceNumber] || null };
      })
    );
    if (results.length > 0) {
      try {
        localStorage.setItem(
          resultsCacheKey(raceId),
          JSON.stringify({ ts: Date.now(), pub: resultsPublishedAt, data: results })
        );
      } catch {
        // plná/nedostupná localStorage – výsledky se příště načtou znovu
      }
    }
    return { raceId, results, fromCache: false };
  })();

  resultsInflight.set(inflightKey, run);
  const settle = () => {
    if (resultsInflight.get(inflightKey) === run) resultsInflight.delete(inflightKey);
  };
  run.then(settle, settle);
  return run;
}

/**
 * Počet publikovaných střelců agregačním dotazem (jen číslo, bez čtení
 * dokumentů) – pro odznak „N střelců" u závodů bez resultsPublishedCount
 * (starší importy). Chybu propaguje.
 */
export async function countRaceResults(raceId: string): Promise<number> {
  const agg = await getCountFromServer(collection(db, `races/${raceId}/results_entries`));
  return agg.data().count;
}
